import { useSessionStore } from "@auth/sessionStore";
import { AsyncBoundary } from "@components/AsyncBoundary";
import { useUser } from "@context/UserContext";
import {
  faArrowRightArrowLeft,
  faCalendarDay,
  faChevronDown,
  faClockRotateLeft,
  faPlay,
  faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Swipe } from "@scribenote/brian-design-system";
import { useNavigate } from "@tanstack/react-router";
import { canAccessReplay } from "@users/userClient";
import { formatDate, formatTime } from "@utils/format-date";
import { useNow } from "@utils/use-now";
import { useResetOnChange } from "@utils/use-reset-on-change";
import { useActiveSubject } from "@workspace/useActiveSubject";
import { useLayoutEffect, useRef, useState } from "react";

import { groupVisitHistory } from "./groupVisitHistory";
import { PatientCard } from "./PatientCard";
import { isVisitSettled, type TodayVisit } from "./visitsClient";
import {
  useOpenVisits,
  usePatientVisitHistory,
  useTodayVisits,
} from "./visitsQueries";

/**
 * The persistent Patient Flow rail: the day's visits as index cards laid out on
 * the desk. It fills whatever height is left in the workspace's right column below
 * `CompanionControls`, so the column owns the 360px width rather than the rail
 * itself. Nothing here is paper, so the rail's own chrome writes in
 * `text-ink-inverse` on the desk; each query suspends, so the list is guarded by an
 * AsyncBoundary whose skeleton stands in for the cards.
 *
 * Always the whole day: it does not narrow to the open room (WEB-3288).
 *
 * When a patient is open, a header icon toggles between clinic-wide "Today's
 * Visits" and that patient's "Recent visits", the one place the rail narrows to the
 * patient in view — the icon shows the view it switches TO, matching the
 * completed/in-progress swap below. History only fetches once toggled to, so
 * opening a patient never costs a round-trip nobody asked for.
 */
export const PatientFlowRail = () => {
  const activeSubject = useActiveSubject();
  const [view, setView] = useState<"today" | "history">("today");

  const patientId = activeSubject?.kind === "patient" ? activeSubject.id : null;
  // A different patient (or none) shouldn't inherit the previous patient's
  // history view — snap back to the clinic-wide list.
  useResetOnChange(patientId, () => setView("today"));
  const showHistory = view === "history" && patientId !== null;

  return (
    <aside className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex items-center justify-between px-1 pb-3">
        <h2 className="font-display text-lg font-medium text-ink-inverse">
          {showHistory ? "Recent visits" : "Today's Visits"}
        </h2>
        {patientId && (
          <button
            type="button"
            onClick={() => setView(showHistory ? "today" : "history")}
            aria-pressed={showHistory}
            aria-label={
              showHistory ? "Show today's visits" : "Show recent visits"
            }
            title={showHistory ? "Today's visits" : "Recent visits"}
            data-testid="visit-history-toggle"
            className="flex size-8 cursor-pointer items-center justify-center rounded-full text-sm text-ink-inverse/70 transition-colors hover:bg-paper/10 hover:text-ink-inverse focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-lite"
          >
            <FontAwesomeIcon
              icon={showHistory ? faCalendarDay : faClockRotateLeft}
              aria-hidden="true"
            />
          </button>
        )}
      </div>
      <AsyncBoundary
        skeleton={<RailSkeleton />}
        fallback={(retry) => (
          <p className="px-1 text-sm text-ink-inverse/80">
            Couldn&rsquo;t load patient flow.{" "}
            <button type="button" onClick={retry} className="underline">
              Retry
            </button>
          </p>
        )}
      >
        {showHistory ? (
          <VisitHistoryList patientId={patientId} />
        ) : (
          <PatientFlowList />
        )}
      </AsyncBoundary>
    </aside>
  );
};

/** The `space-y-4` gap between the still-open section and today's list, in px —
 * part of the space it takes but not part of its `offsetHeight`. */
const STILL_OPEN_GAP = 16;

const PatientFlowList = () => {
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const { data: visits } = useTodayVisits(sessionKey);
  const { data: openVisits } = useOpenVisits(sessionKey);
  const [showCompleted, setShowCompleted] = useState(false);

  // True once something has completed and we still have vertical room: completed
  // visits show inline (see CompletedSection) instead of behind the swap button.
  // Measured against the in-progress list only, so expanding the inline section
  // can't change the result and flip the mode (avoids flicker). Optimistically
  // true to render inline first; the layout effect corrects it before paint.
  const [hasRoom, setHasRoom] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inProgressRef = useRef<HTMLDivElement>(null);
  const stillOpenRef = useRef<HTMLDivElement>(null);

  // Re-measure whenever the in-progress content or the viewport changes size.
  // When the swap view is open the in-progress list is unmounted, so the
  // observer never fires and hasRoom stays frozen — the mode that opened the
  // swap holds until the user returns.
  useLayoutEffect(() => {
    const viewport = scrollRef.current;
    const inProgress = inProgressRef.current;
    if (!viewport || !inProgress) return;

    const measure = () => {
      // The still-open section shares the viewport but sits outside the
      // in-progress list, so the space it takes isn't space today's cards can
      // use. Without subtracting it the rail picks inline mode on a viewport
      // that's already full and pushes in-progress cards below the fold.
      const stillOpen = stillOpenRef.current;
      const taken = stillOpen ? stillOpen.offsetHeight + STILL_OPEN_GAP : 0;
      // A few px of slack so a list that exactly fills the rail still counts as
      // having room rather than oscillating on sub-pixel rounding.
      setHasRoom(inProgress.scrollHeight <= viewport.clientHeight - taken + 2);
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    observer.observe(inProgress);
    // Collapsing the section frees real space, so it has to re-measure. Unlike
    // the inline Completed section this can't oscillate: the still-open
    // section's height doesn't depend on the mode it feeds.
    if (stillOpenRef.current) observer.observe(stillOpenRef.current);
    return () => observer.disconnect();
  });

  // Never narrowed to the open room (WEB-3288); room context lives in the pane.
  const shown = visits;

  const stillOpen = openVisits ?? [];
  const stillOpenSection =
    stillOpen.length > 0 ? (
      <StillOpenSection ref={stillOpenRef} visits={stillOpen} />
    ) : null;

  // Shown alongside the empty state: a clinic with nothing booked today can
  // still be holding a visit open from last week.
  if (shown.length === 0)
    return (
      // overflow-x-hidden backstop: overflow-y-auto forces overflow-x to
      // compute as auto per spec, which would otherwise let stray horizontal
      // overflow show as a scrollbar here.
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overflow-x-hidden px-1 pt-1">
        {stillOpenSection}
        <p className="px-1 text-sm text-ink-inverse/70">
          Nothing on the book today.
        </p>
      </div>
    );

  // A visit settles out of the active list once it's completed or cancelled
  // (see isVisitSettled); both land in the "Completed" section below, in their
  // existing scheduled-time order. The card marks cancelled ones distinctly.
  const completed = shown.filter(isVisitSettled);
  const inProgress = shown.filter((visit) => !isVisitSettled(visit));

  // No room: fall back to the swap button that swaps the whole list. Offer it
  // once something has completed, and keep it while viewing completed so there's
  // always a way back to in-progress.
  const swapMode = !hasRoom;
  const showSwapCompleted = swapMode && showCompleted;
  const showSwapButton = swapMode && (completed.length > 0 || showCompleted);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollRef}
        // Side padding, not just right: a selected card's tone ring and float
        // shadow sit outside its box and would otherwise clip on the edges.
        // overflow-x-hidden backstop: overflow-y-auto forces overflow-x to
        // compute as auto per spec, which would otherwise let stray
        // horizontal overflow show as a scrollbar here.
        // pt-[5px], not pt-1: a selected top card lifts -translate-y-0.5 (2px)
        // on top of its own ring-[3px], so the first card needs 5px of headroom
        // to clear scrolled-to-top without clipping the ring.
        className="min-h-0 flex-1 space-y-4 overflow-y-auto overflow-x-hidden px-1 pt-[5px]"
      >
        {/* Outside the swap so it survives both views, and outside inProgressRef
            so the measurement keeps sizing today's list alone. */}
        {stillOpenSection}
        {showSwapCompleted ? (
          completed.length === 0 ? (
            <p className="px-1 text-sm text-ink-inverse/70">
              Nothing finished yet.
            </p>
          ) : (
            completed.map((visit) => (
              <PatientCard key={visit.id} visit={visit} />
            ))
          )
        ) : (
          <>
            <div ref={inProgressRef} className="space-y-4">
              {inProgress.length === 0 ? (
                <p className="px-1 text-sm text-ink-inverse/70">
                  All visits complete.
                </p>
              ) : (
                inProgress.map((visit) => (
                  <PatientCard key={visit.id} visit={visit} />
                ))
              )}
            </div>
            {hasRoom && completed.length > 0 && (
              <CompletedSection visits={completed} />
            )}
          </>
        )}
      </div>
      {showSwapButton && (
        <button
          type="button"
          onClick={() => setShowCompleted((c) => !c)}
          aria-pressed={showCompleted}
          className="flex w-full shrink-0 cursor-pointer items-center gap-2 border-t border-paper/10 px-1 pb-1 pt-2 text-left text-annotation-caps text-ink-inverse/70 transition-colors hover:text-ink-inverse focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-lite"
        >
          <span className="flex-1">
            {showCompleted
              ? `In progress (${inProgress.length})`
              : `Completed (${completed.length})`}
          </span>
          <FontAwesomeIcon
            icon={faArrowRightArrowLeft}
            aria-hidden="true"
            className="shrink-0 text-xs"
          />
        </button>
      )}
    </div>
  );
};

/**
 * "Still open (n)" — visits from earlier days that were started but never
 * finished (WEB-3271); nothing closes a visit automatically, so they otherwise
 * leave the board at midnight unsettled. Expanded by default, unlike Completed:
 * settled work can hide, a stuck visit is the point.
 *
 * A light paper strip laid on the desk (gold-ruled, floating) rather than a
 * recess cut into it: leftover work has to be the brightest thing in the rail,
 * and dark ink on paper is the only way to say so calmly. Being paper is also
 * what makes the marker available - a `flag` Swipe multiplies, so it needs a
 * light ground to read at all, and it lets the eyebrow say the state in a word
 * instead of coloring the label and hoping.
 */
const StillOpenSection = ({
  visits,
  ref,
}: {
  visits: TodayVisit[];
  /** The rail measures this section's height out of the space available to
   * today's cards, so it needs the rendered node. */
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const [open, setOpen] = useState(true);

  return (
    <div
      ref={ref}
      className="space-y-4 rounded-paper bg-recess p-2 shadow-float ring-1 ring-gold"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid="still-open-toggle"
        className="flex w-full cursor-pointer items-center gap-2 rounded-paper px-1 text-left text-annotation-caps text-ink transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        <FontAwesomeIcon
          icon={faTriangleExclamation}
          aria-hidden="true"
          className="shrink-0 text-xs text-ink-flag"
        />
        <span className="flex-1">
          <Swipe tone="flag">Still open ({visits.length})</Swipe>
        </span>
        <FontAwesomeIcon
          icon={faChevronDown}
          aria-hidden="true"
          className={`shrink-0 text-xs text-annotation transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open &&
        visits.map((visit) => <PatientCard key={visit.id} visit={visit} />)}
    </div>
  );
};

/**
 * Inline "Completed (n)" section appended after the in-progress cards when the
 * rail has room to spare. Collapsible, expanded by default. Lives in the same
 * scroll list as the in-progress cards.
 */
const CompletedSection = ({ visits }: { visits: TodayVisit[] }) => {
  const [open, setOpen] = useState(true);

  return (
    <div className="space-y-4 border-t border-paper/10 pt-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center gap-2 px-1 text-left text-annotation-caps text-ink-inverse/70 transition-colors hover:text-ink-inverse focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-lite"
      >
        <span className="flex-1">Completed ({visits.length})</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          aria-hidden="true"
          className={`shrink-0 text-xs transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open &&
        visits.map((visit) => <PatientCard key={visit.id} visit={visit} />)}
    </div>
  );
};

/**
 * The open patient's recent visits, filling the rail when toggled to.
 * Split into "Upcoming" / "In progress" / "Completed" (see groupVisitHistory;
 * cancelled visits land in Completed, tagged with an outlined mono pill: these
 * rows are annotations written straight onto the desk, not paper objects).
 * In-progress and completed rows carry an explicit Replay button — the row
 * itself never navigates — shown only to users who can access replay (an
 * underway visit replays "so far", same as PatientCard's entry link);
 * upcoming and cancelled visits have nothing to replay.
 */
const VisitHistoryList = ({ patientId }: { patientId: string }) => {
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const { currentUser } = useUser();
  const now = useNow();
  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePatientVisitHistory(sessionKey, patientId, true);
  const visits = data?.pages.flat();

  if (isLoading) {
    return (
      <div className="space-y-2 px-1">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="h-10 animate-pulse rounded-paper bg-paper/10"
          />
        ))}
      </div>
    );
  }

  // This query doesn't suspend, so the rail's AsyncBoundary never sees its
  // failures. Without this branch an error would render as the empty state
  // ("No visits on record yet.").
  if (isError)
    return (
      <p className="px-1 text-sm text-ink-inverse/80">
        I couldn&rsquo;t load recent visits.{" "}
        <button
          type="button"
          onClick={() => void refetch()}
          className="underline"
        >
          Retry
        </button>
      </p>
    );

  if (!visits || visits.length === 0)
    return (
      <p className="px-1 text-sm text-ink-inverse/70">
        No visits on record yet.
      </p>
    );

  const { upcoming, inProgress, completed } = groupVisitHistory(visits, now);
  const showReplay = canAccessReplay(currentUser);

  return (
    // overflow-x-hidden backstop: overflow-y-auto forces overflow-x to
    // compute as auto per spec, which would otherwise let stray horizontal
    // overflow show as a scrollbar here.
    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overflow-x-hidden pr-1 pt-1">
      {upcoming.length > 0 && (
        <HistoryGroup label="Upcoming" visits={upcoming} showReplay={false} />
      )}
      {inProgress.length > 0 && (
        <HistoryGroup
          label="In progress"
          visits={inProgress}
          showReplay={showReplay}
        />
      )}
      {completed.length > 0 && (
        <HistoryGroup
          label="Completed"
          visits={completed}
          showReplay={showReplay}
        />
      )}
      {hasNextPage && (
        <button
          type="button"
          onClick={() => void fetchNextPage()}
          disabled={isFetchingNextPage}
          data-testid="visit-history-load-more"
          className="w-full cursor-pointer px-1 pb-1 text-left text-xs font-medium text-ink-inverse/70 transition-colors hover:text-ink-inverse disabled:opacity-50"
        >
          {isFetchingNextPage ? "Loading…" : "Load older visits"}
        </button>
      )}
    </div>
  );
};

const HistoryGroup = ({
  label,
  visits,
  showReplay,
}: {
  label: string;
  visits: TodayVisit[];
  showReplay: boolean;
}) => {
  const navigate = useNavigate();

  return (
    <div className="space-y-1">
      <p className="px-1 text-annotation-caps text-ink-inverse/50">
        {label} ({visits.length})
      </p>
      {visits.map((visit) => (
        <div
          key={visit.id}
          data-testid={`history-visit-${visit.id}`}
          className="flex items-center gap-2 rounded-paper px-1 py-1.5 text-sm text-ink-inverse/90"
        >
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">
              {visit.visitType}
              {visit.reason ? ` — ${visit.reason}` : ""}
            </p>
            <p className="truncate font-mono text-fine-print text-ink-inverse/60">
              {visit.scheduledStart
                ? `${formatDate(visit.scheduledStart)} · ${formatTime(visit.scheduledStart)}`
                : "Unscheduled"}
              {visit.assignedUserName ? ` · ${visit.assignedUserName}` : ""}
            </p>
          </div>
          {visit.cancelledAt && (
            // Not a kit Tag: Tag bakes `bg-card`, so on the desk it would be a
            // white chip, and its map has no on-desk inverse tone. One rail is
            // not enough to earn one.
            <span className="shrink-0 rounded-full border border-paper/20 px-1.5 py-0.5 text-annotation-caps text-ink-inverse/80">
              Canceled
            </span>
          )}
          {showReplay && !visit.cancelledAt && (
            <button
              type="button"
              onClick={() =>
                navigate({ to: "/replay", search: { visit: visit.id } })
              }
              data-testid={`replay-visit-${visit.id}`}
              className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full bg-paper/10 px-2.5 py-1 text-xs font-medium text-ink-inverse/80 transition-colors hover:bg-paper/20 hover:text-ink-inverse focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-lite"
            >
              <FontAwesomeIcon icon={faPlay} aria-hidden="true" />
              Replay
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

/* Hand-rolled: the rail is on the desk, and the kit `Skeleton`'s two surfaces
   are both paper ones - `bar`'s recess well vanishes into the desk and `sheet`
   would be a stack of blank white cards. A paper tint is the on-desk blank. */
const RailSkeleton = () => (
  <div className="flex flex-col gap-4 px-1">
    {Array.from({ length: 4 }).map((_, i) => (
      <div
        key={i}
        // Card-height blanks: a ruled face plus its band comes to ~7.5rem.
        className="h-30 animate-pulse rounded-paper bg-paper/10"
        style={{ animationDelay: `${i * 80}ms` }}
      />
    ))}
  </div>
);
